import { type FormEvent, useEffect, useReducer, useState } from 'react';
import { useConfig } from '../../config/ConfigContext';
import { storeLastSearchPrepackage } from '../common/lastSearch';
import { trackSearchClick } from '../common/tracking';
import type { PrepackageSearchState } from '../types';
import { useDestinationHandler } from './hooks/useDestinationHandler';
import { usePrepackageAutocompleteMapper } from './hooks/usePrepackageAutocompleteMapper';
import { usePrepackageFacets } from './hooks/usePrepackageFacets';
import { usePrepackageInitialState } from './hooks/usePrepackageInitialState';
import { usePrepackageValidation } from './hooks/usePrepackageValidation';
import { type PrepackageAutoCompleteData, PrepackageSearchForm } from './PrepackageSearchForm';
import actions, { reducer } from './prepackageActions';
import { ppDefaultState } from './prepackageDefaultState';
import { buildParams } from './utils/buildParams';
import { buildPrepackageTrackingItem } from './utils/buildPrepackageTrackingItem';

type PrePackageSearchProps = {
    prefill?: Partial<PrepackageSearchState>;
    position: string;
    unleash?: Record<string, boolean> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const PrepackageSearch = ({ prefill, position, unleash }: PrePackageSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, ppDefaultState);

    const paxEnabled = unleash?.numPax ?? false;

    const ready = usePrepackageInitialState(dispatch, prefill);

    const { ppAutocompleteUrl, ppFacetUrl, ppResultUrl } = useConfig();

    const facets = state.facetData?.[buildParams(state, paxEnabled)];
    const lookupCount = (id: string): number => (facets ? facets[id] || 0 : 0);

    const { validateState, invalid, validationErrors } = usePrepackageValidation(state, pristine);
    usePrepackageFacets({ state, dispatch, facetUrl: ppFacetUrl, paxEnabled });
    const autocompleteMapper = usePrepackageAutocompleteMapper(lookupCount);
    const handleOnSelect = useDestinationHandler(dispatch);

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        setPristine(false);
        if (validateState()) {
            trackSearchClick('Search prepackage', '/reise/pakkereiser', 'prepackage', position, buildPrepackageTrackingItem(state));
            storeLastSearchPrepackage({ ...state, facetData: undefined });
        } else {
            event.preventDefault();
        }
    };

    const [hasNoOffers, setHasNoOffers] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (facets && state.destinationId) {
                const count = lookupCount(state.destinationId);
                setHasNoOffers(count === 0);
            }
        }, 10);
        return () => clearTimeout(timeout);
    }, [facets, state.destinationId]);

    const autoCompleteData: PrepackageAutoCompleteData = {
        ppAutocompleteUrl: ppAutocompleteUrl,
        autocompleteMapper: autocompleteMapper,
        onDestinationSelect: handleOnSelect,
    };

    return (
        <PrepackageSearchForm
            state={state}
            ready={ready}
            ppResultUrl={ppResultUrl}
            invalid={invalid}
            validationErrors={validationErrors}
            hasNoOffers={hasNoOffers}
            paxEnabled={paxEnabled}
            autoCompleteData={autoCompleteData}
            onSubmit={handleSubmit}
            onSearchTypeChange={(searchType) => dispatch(actions.setSearchType(searchType))}
            onOriginAirportChange={(originAirport) => dispatch(actions.setOriginAirport(originAirport))}
            onToggleTravelMonth={(month) => dispatch(actions.toggleTravelMonth(month))}
            onDurationChange={(duration) => dispatch(actions.setDuration(duration))}
            onDatesChange={(dates) => dispatch(actions.setDates(dates))}
            onPaxChange={(adults, childrenAges) =>
                dispatch(actions.setPaxCombination({ numberOfAdults: adults, agesOfChildren: childrenAges }))
            }
        />
    );
};
